/**
 * useNotificationSSE
 *
 * Opens a Server-Sent Events connection to /api/notifications/stream
 * and invalidates the three notification-count React Query cache keys
 * whenever the server pushes an event — so Bell badge counts update
 * instantly without waiting for the next polling interval.
 *
 * On connection error the hook reconnects with exponential back-off
 * (1 s → 2 s → 4 s … capped at 30 s). The existing polling queries in
 * layout.tsx serve as a reliable fallback if SSE is unavailable.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

type NotificationEvent =
  | { type: "messages" }
  | { type: "support" }
  | { type: "alerts" };

const QUERY_KEY_MAP: Record<NotificationEvent["type"], string[]> = {
  messages: ["/api/messages/unread-count"],
  support:  ["/api/support/unread-count"],
  alerts:   ["/api/admin/system-alerts/unread-count", "/api/admin/system-alerts"],
};

export function useNotificationSSE(enabled = true): void {
  const queryClient = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const retryDelayRef = useRef<number>(INITIAL_RETRY_MS);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const destroyedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    destroyedRef.current = false;
    retryDelayRef.current = INITIAL_RETRY_MS;

    function connect() {
      if (destroyedRef.current) return;

      const es = new EventSource("/api/notifications/stream", { withCredentials: true });
      esRef.current = es;

      es.onopen = () => {
        retryDelayRef.current = INITIAL_RETRY_MS;
      };

      es.onmessage = (event) => {
        try {
          const notification: NotificationEvent = JSON.parse(event.data);
          const keys = QUERY_KEY_MAP[notification.type];
          if (!keys) return;
          for (const key of keys) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        } catch {
          // Malformed event — ignore
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (destroyedRef.current) return;

        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, MAX_RETRY_MS);

        retryTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    }

    connect();

    return () => {
      destroyedRef.current = true;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [enabled, queryClient]);
}
